import { Plus, Star, Trash2 } from "lucide-react";
import { useState } from "react";

import {
  useCreatePolicy,
  useDeletePolicy,
  useEditPolicy,
  useJDPolicies,
  type JDPolicy,
} from "@/api/jd-question-queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";

/**
 * Pick policies — DESIGN §20.9. Per bucket, the percent of a JD interview drawn from the JD's own
 * questions; the rest comes from the JD's stacks in the general pool. A JD follows the default unless
 * its Resource Manager chose another on its Pick policy tab. Editing one changes every JD that uses it,
 * which is why this sits with the other picker settings, under MANAGE_PICKER (§20.10).
 */

const BUCKETS = ["technical", "project_domain", "problem_solving", "soft_skill"] as const;

function sharesOf(policy: JDPolicy | null): Record<string, string> {
  return Object.fromEntries(BUCKETS.map((b) => [b, String(policy?.shares[b] ?? 0)]));
}

function toShares(draft: Record<string, string>): Record<string, number> {
  return Object.fromEntries(
    BUCKETS.map((b) => [b, Math.max(0, Math.min(100, Math.round(Number(draft[b]) || 0)))]),
  );
}

function PolicyRow({ policy }: { policy: JDPolicy }) {
  const edit = useEditPolicy();
  const remove = useDeletePolicy();
  const [name, setName] = useState(policy.name);
  const [draft, setDraft] = useState(sharesOf(policy));
  const dirty =
    name.trim() !== policy.name || BUCKETS.some((b) => Number(draft[b]) !== (policy.shares[b] ?? 0));

  return (
    <tr className="border-bank-border border-t align-top">
      <td className="px-3 py-2">
        <Input
          aria-label="Policy name"
          className="w-48"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <p className="text-bank-muted mt-1 text-xs">
          {policy.is_default ? "Default · " : ""}
          {policy.used_by} {policy.used_by === 1 ? "JD chose it" : "JDs chose it"}
        </p>
        <ErrorNote error={edit.error ?? remove.error} />
      </td>
      {BUCKETS.map((b) => (
        <td key={b} className="px-3 py-2">
          <div className="flex items-center gap-1">
            <Input
              aria-label={`${policy.name}: ${b} percent from the JD`}
              type="number"
              min={0}
              max={100}
              step={10}
              className="w-20 font-mono"
              value={draft[b] ?? "0"}
              onChange={(e) => setDraft({ ...draft, [b]: e.target.value })}
            />
            <span className="text-bank-muted text-xs">%</span>
          </div>
        </td>
      ))}
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="primary"
            disabled={!dirty || edit.isPending}
            onClick={() => edit.mutate({ id: policy.id, name: name.trim(), shares: toShares(draft) })}
          >
            Save
          </Button>
          {!policy.is_default && (
            <>
              <Button
                size="sm"
                variant="ghost"
                title="Every JD with no policy of its own follows the default"
                disabled={edit.isPending}
                onClick={() => edit.mutate({ id: policy.id, is_default: true })}
              >
                <Star className="size-3.5" aria-hidden="true" />
                Make default
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Delete ${policy.name}`}
                disabled={remove.isPending || policy.used_by > 0}
                title={policy.used_by > 0 ? "Give its JDs another policy first" : "Delete"}
                onClick={() => remove.mutate(policy.id)}
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
              </Button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function NewPolicy() {
  const create = useCreatePolicy();
  const [name, setName] = useState("");
  const [draft, setDraft] = useState(sharesOf(null));
  return (
    <tr className="border-bank-border bg-bank-surface-2 border-t align-top">
      <td className="px-3 py-2">
        <Input
          aria-label="New policy name"
          className="w-48"
          placeholder="A new policy"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <ErrorNote error={create.error} />
      </td>
      {BUCKETS.map((b) => (
        <td key={b} className="px-3 py-2">
          <Input
            aria-label={`New policy: ${b} percent from the JD`}
            type="number"
            min={0}
            max={100}
            step={10}
            className="w-20 font-mono"
            value={draft[b] ?? "0"}
            onChange={(e) => setDraft({ ...draft, [b]: e.target.value })}
          />
        </td>
      ))}
      <td className="px-3 py-2 text-right">
        <Button
          size="sm"
          disabled={!name.trim() || create.isPending}
          onClick={() =>
            create.mutate(
              { name: name.trim(), shares: toShares(draft), note: null },
              { onSuccess: () => (setName(""), setDraft(sharesOf(null))) },
            )
          }
        >
          <Plus className="size-3.5" aria-hidden="true" />
          Add
        </Button>
      </td>
    </tr>
  );
}

const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase";

export function JDPoliciesPanel() {
  const policies = useJDPolicies();
  return (
    <Panel className="overflow-hidden">
      <PanelHeader title="Pick policies — the JD share of each round" />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bank-surface-2 border-bank-border border-b text-left">
            <tr>
              <th className={TH}>policy</th>
              {BUCKETS.map((b) => (
                <th key={b} className={TH}>
                  {b}
                </th>
              ))}
              <th className={TH} />
            </tr>
          </thead>
          <tbody>
            {(policies.data ?? []).map((p) => (
              <PolicyRow key={`${p.id}:${p.updated_at ?? ""}`} policy={p} />
            ))}
            <NewPolicy />
          </tbody>
        </table>
      </div>
      <div className="space-y-2 p-4 pt-2">
        <ErrorNote error={policies.error} />
        <p className="text-bank-muted text-xs">
          The percent of each bucket an interview for a JD draws from the JD's own questions — rounded down,
          but never to none while the JD has a question there. The rest, and whatever the JD cannot fill,
          comes from the JD's stacks in the bank.
        </p>
      </div>
    </Panel>
  );
}
